import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCreateEmployee } from '../../hooks/use-employees';
import { basicInfoDefaults } from '../../schemas/basic-info-schema';
import {
  createValuesToInput,
  employeeCreateDefaults,
  employeeCreateSchema,
  EmployeeCreateValues,
  OverviewValues,
} from '../../schemas/employee-form-schema';
import { requiredFieldsOf } from './required-fields';
import { BasicInfoStep } from './steps/basic-info-step';
import { TabForm } from './tab-form';
import { useTranslatedResolver } from './translate-form-errors';
import { APP_PATHS } from '@/constant/app-paths';

/** The step's fields are typed on the basic info; the add form carries the business email / phone too. */
type CreateFormValues = OverviewValues & EmployeeCreateValues;

const CREATE_DEFAULTS = { ...basicInfoDefaults, ...employeeCreateDefaults };
const REQUIRED_FIELDS = requiredFieldsOf(employeeCreateSchema, CREATE_DEFAULTS);

/**
 * Adding a person asks only for the minimum, like adding a user in Blocks OS: name, business email /
 * phone and where they sit in the organization. The login user is invited, and the rest of the profile,
 * the HR terms and the roles are filled in afterwards.
 */
export const EmployeeCreateForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const resolver = useTranslatedResolver<CreateFormValues>(employeeCreateSchema);
  const form = useForm<CreateFormValues>({ resolver, defaultValues: CREATE_DEFAULTS });
  const { mutateAsync, isPending } = useCreateEmployee();

  const handleSave = async (values: CreateFormValues) => {
    await mutateAsync(createValuesToInput(values));
    navigate(APP_PATHS.PEOPLE);
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <p className="text-sm text-muted-foreground">{t('ADD_EMPLOYEE_HINT')}</p>
      <TabForm
        form={form}
        onSave={handleSave}
        isSaving={isPending}
        submitLabel="CREATE"
        requiredFields={REQUIRED_FIELDS}
      >
        <BasicInfoStep isEditMode={false} variant="create" />
      </TabForm>
    </div>
  );
};
